import React, { useState } from 'react';
import { motion } from 'motion/react';
import { useToast } from "../hooks/useToast";
import Logo from '../components/logo/Logo';
import { Input } from '../components/common/Input';
import { Button } from '../components/common/Button';
import { ArrowRightIcon } from '@radix-ui/react-icons';
import { forgotPassword } from '../api/auth';

const ForgotPassword = () => {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await forgotPassword(email);
      setSubmitted(true);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || 'Failed to process request',
        type: "error",
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div 
      className="min-h-screen grid lg:grid-cols-2 bg-gradient-to-br from-primary/5 via-background to-secondary/5"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Left side - Branding */}
      <div className="hidden lg:flex flex-col items-center justify-center p-8 relative overflow-hidden">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="relative z-10"
        >
          <div className="flex justify-center">
            <Logo />
          </div>
          <p className="mt-6 text-lg text-center text-muted-foreground max-w-sm">
            "For I am not Eternity, but a human being;<br/>a part of the whole, as an hour is part of the day. I must come like the hour, and like the hour must pass!"
          </p>
        </motion.div>
        
        {/* Background decoration */}
        <div className="absolute inset-0 z-0">
          <motion.div
            className="absolute inset-0 bg-primary/5 backdrop-blur-3xl"
            // initial={{ opacity: 0, scale: 0.8 }}
            // animate={{ opacity: 1, scale: 1 }}
            // transition={{ delay: 0.3, duration: 0.8 }}
          />
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full bg-primary/10 backdrop-blur-3xl"
              style={{
                width: '30vw',
                height: '30vw',
                top: ['20%', '50%', '70%'][i],
                left: ['20%', '60%', '30%'][i],
              }}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 + (i * 0.2), duration: 1 }}
            />
          ))}
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex items-center justify-center p-8">
        <motion.div
          className="w-full max-w-md space-y-8"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <div className="lg:hidden mb-8 flex justify-center">
            <Logo />
          </div>

          <div className="space-y-2 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">Forgot Password</h1>
            <p className="text-sm text-muted-foreground">
              Enter your email address and we'll send you instructions to reset your password
            </p>
          </div>

          {!submitted ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full button-glow"
                disabled={loading}
              >
                {loading ? (
                  <motion.div
                    className="h-5 w-5 border-2 border-current border-r-transparent rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  />
                ) : (
                  <>
                    Send Reset Instructions
                    <ArrowRightIcon className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>
          ) : (
            <div className="text-center p-4 rounded-lg bg-primary/5">
              <p className="text-sm text-muted-foreground">
                Check your email for password reset instructions. The link in the email will expire in 24 hours.
              </p>
            </div>
          )}

          <div className="text-center text-sm">
            <a 
              href="/login" 
              className="font-medium text-primary hover:text-primary/90 transition-colors"
            >
              Back to Login
            </a>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ForgotPassword; 